/**
 * Nurse Discharge (Non-Visit) — static options + empty form state.
 * Sections mirror Eval & Orders → Nurse Discharge (Non-Visit) screenshots.
 */

import { emptyNurseNotesCommonHeader } from "./nurseNotesCommonHeaderUtils";
import { emptyWoundAssessmentValues } from "./woundAssessmentContent";

export const today = () => new Date().toISOString().slice(0, 10);

export const toggleInList = (list, value) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
};

export const DISCIPLINE_OPTIONS = ["RN", "LVN/LPN", "Other"];

export const NON_VISIT_REASON_OPTIONS = [
  "Patient/caregiver refused visit",
  "Patient unavailable for visit",
  "Physician/allowed practitioner directed discharge",
  "Patient hospitalized",
  "Patient moved out of service area",
  "Transferred to another provider",
  "Patient died",
  "Other",
];

export const DISCHARGE_DISPOSITION_OPTIONS = [
  "Remained in community without skilled services from another Medicare-certified HHA or non-institutional hospice.",
  "Remained in community receiving skilled services from another Medicare-certified HHA.",
  "Admitted to inpatient facility (Hospital, SNF, Inpatient Rehab, LTCH).",
  "Admitted to institutional hospice facility.",
  "Patient deceased.",
  "Unknown.",
];

export const GENERAL_CONDITION_OPTIONS = [
  "Stable",
  "Improving",
  "Declining",
  "Unable to determine",
];

export const PAIN_STATUS_OPTIONS = [
  "Controlled",
  "Uncontrolled",
  "Unable to determine",
];

export const MOBILITY_STATUS_OPTIONS = [
  "Maintained",
  "Improved",
  "Declined",
  "Unable to determine",
];

export const COGNITIVE_STATUS_OPTIONS = [
  "WNL/Baseline",
  "Changed",
  "Unable to determine",
];

export const RESPIRATORY_STATUS_OPTIONS = [
  "WNL/Baseline",
  "Changed",
  "Unable to determine",
];

export const DISCHARGE_REASON_OPTIONS = [
  "Goals met / maximal potential reached",
  "Patient no longer homebound",
  "Patient no longer requires skilled nursing services",
  "Patient/caregiver independent with care",
  "Patient refuses further care",
  "Unsafe home environment",
  "Non-compliance with plan of care",
  "Physician request",
  "Admitted to hospital",
  "Transferred to SNF/Rehab",
  "Transferred to Hospice",
  "Patient expired",
  "Other",
];

export const PLAN_OF_CARE_GOAL_OPTIONS = [
  "Goals met",
  "Goals partially met",
  "Goals not met",
  "Unable to determine (due to non-visit discharge)",
];

export const MEDICATION_RECONCILE_OPTIONS = [
  "Medication profile reconciled at time of discharge",
  "Unable to reconcile due to non-visit",
];

export const PHYSICIAN_NOTIFY_OPTIONS = [
  "Physician notified of discharge",
  "Discharge ordered by physician",
];

export const CAREGIVER_COMM_OPTIONS = [
  "Patient/Caregiver notified of discharge",
  "Unable to reach Patient/Caregiver",
];

export const TRANSITION_INFO_OPTIONS = [
  "Clinical summary",
  "Care plan goals",
  "Medication profile",
  "Pending tests/appointments",
];

export const OASIS_STATUS_OPTIONS = [
  "OASIS Assessment Required",
  "OASIS Assessment Not Required",
];

export const OASIS_SUBMISSION_OPTIONS = [
  "Ready for submission",
  "Pending physician signature",
];

export const DOCUMENTATION_REVIEW_OPTIONS = [
  "All visit notes complete and signed",
  "All orders signed by physician",
  "Medication profile accurate",
  "Supplies/equipment returned/picked up",
  "Notice of Medicare Non-Coverage (NOMNC) delivered",
  "Patient/caregiver given copy of discharge instructions",
  "Physician notified of discharge",
  "Other disciplines notified of discharge",
  "Final clinical summary completed",
  "Discharge OASIS completed (if applicable)",
  "Case manager notified",
];

/** Full editable Nurse Discharge (Non-Visit) payload (JSON on dedicated row). */
export const emptyRnDischargeNonVisitPayload = () => ({
  commonHeader: emptyNurseNotesCommonHeader(),

  /** Episode extras (patient/payer basics live in commonHeader). */
  socDate: "",
  certPeriodFrom: "",
  certPeriodTo: "",
  documentationTime: "",

  /** Legacy snapshot fields retained for older payloads / common-header seed. */
  patientNameDisplay: "",
  dateOfBirth: "",
  mrn: "",
  primaryPayer: "",

  dischargeDate: "",
  dischargeTime: "",
  discipline: [],
  disciplineOther: "",
  clinicianName: "",
  credentials: "",
  nonVisitReasons: [],
  nonVisitReasonOther: "",
  nonVisitExplanation: "",

  dischargeDisposition: "",
  subsequentProviderName: "",
  subsequentProviderPhone: "",
  subsequentProviderAddress: "",

  generalCondition: "",
  painStatus: "",
  mobilityStatus: "",
  cognitiveStatus: "",
  respiratoryStatus: "",
  otherConcerns: "",

  ...emptyWoundAssessmentValues(),

  dischargeReasons: [],
  dischargeReasonOther: "",
  dischargeJustification: "",

  planOfCareGoalStatus: "",
  unresolvedProblems: "",
  recommendedContinuedCare: "",

  medicationReconcile: [],
  medicationConcerns: "",
  outstandingOrders: "",

  physicianNotifyFlags: [],
  physicianNotifyDate: "",
  physicianNotifyTime: "",
  physicianNotifyMethod: "",
  physicianNotifyPerson: "",
  physicianNotifyResponse: "",

  caregiverCommFlags: [],
  caregiverCommDate: "",
  caregiverCommTime: "",
  caregiverCommMethod: "",
  caregiverCommPerson: "",
  caregiverInstructions: "",
  caregiverUnderstood: false,

  transitionHandoffProvided: false,
  transitionInfo: [],
  transitionDate: "",
  transitionMethod: "",
  transitionRecipient: "",

  oasisStatus: "",
  oasisAssessmentStatus: "",
  oasisTimePoint: "",
  oasisSubmission: [],
  oasisComments: "",

  documentationReview: [],
  finalClinicalSummary: "",

  attestationClinicianName: "",
  attestationDate: "",
  attestationTime: "",

  billingCode: "",
  timeIn: "",
  timeOut: "",
});
